import { useQuery } from "@tanstack/react-query";
import { ShortUser } from "modules/common/model/models";
import { QUERIES } from "modules/common/query";
import { useAuth } from "@clerk/nextjs";
import { API_ROUTES } from "utils/api-route.constants";
import { useFetchWrapper } from "modules/common/hooks/useFetchWrapper";
import { USER_CONTENT_CONFIG } from "modules/common/react-query-configs";

export const useAppUser = (authenticatedUserId?: string) => {
  const fetch = useFetchWrapper();
  const { userId: clerkUserId, isLoaded } = useAuth();
  // Authenticated server components already know the identity that owns their
  // dehydrated queries. Keep that key for this mounted flow so an
  // otherwise-ready page does not sit behind the client auth resource.
  const userId = authenticatedUserId ?? clerkUserId;
  const canFetchAuthenticatedUser =
    isLoaded && (!authenticatedUserId || clerkUserId === authenticatedUserId);

  const getAppUser = async (username: string, userId?: string) => {
    const q = `?username=${username}&user_id=${userId || ""}`;
    return await fetch
      .get(API_ROUTES.USERS.GET + q, null)
      .then((res: ShortUser) => res);
  };

  const {
    data: appUser,
    isPending,
    isFetched,
    isError,
    error,
  } = useQuery<ShortUser>({
    queryKey: [QUERIES.APP_USER, userId],
    queryFn: () => getAppUser("", userId!),
    // Cached server data remains readable while disabled. If the server
    // bootstrap failed, wait for Clerk before making a direct browser API call.
    enabled: !!userId && canFetchAuthenticatedUser,
    ...USER_CONTENT_CONFIG, // 2min stale, no polling, focus-based refresh
  });

  return {
    userId,
    appUser,
    isUserLoaded: isLoaded,
    isPending,
    isFetched,
    isError,
    error,
  };
};
